import type { TyAnchorRect, TyPopupComp } from "@/hooks/usePopupStore";
import usePopupStore from "@/hooks/usePopupStore";
import { type CSSProperties, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./styles.css";

const ANIM_DURATION = 200;

export default function PopupPaper() {
    const { comps, removeComp } = usePopupStore();
    if (comps.length === 0) return null;
    return createPortal(
        <>
            {comps.map((comp, index) => (
                <PopupLayer index={index} key={comp.name} comp={comp} onClose={() => removeComp(comp.name)} />
            ))}
        </>,
        document.body
    );
}

function PopupLayer({ comp, onClose, index }: { comp: TyPopupComp; onClose: () => void; index: number }) {
    const [state, setState] = useState<"closed" | "open">("closed");
    const isAnchor = comp.place === "anchor-below" || comp.place === "anchor-above";

    useEffect(() => {
        const id = requestAnimationFrame(() => setState("open"));
        return () => cancelAnimationFrame(id);
    }, []);

    const handleClose = () => {
        setState("closed");
        setTimeout(onClose, ANIM_DURATION);
    };

    return (
        <>
            <div
                className="popup-backdrop"
                data-cover={comp.cover}
                data-state={state}
                onClick={comp.coverClose ? handleClose : undefined}
                style={{ zIndex: `100${index}`, opacity: state === "open" ? 1 : 0 }}
            />
            {isAnchor ? (
                <AnchorPopup index={index} comp={comp} state={state} onClose={handleClose} />
            ) : (
                <ViewportPopup index={index} comp={comp} state={state} onClose={handleClose} />
            )}
        </>
    );
}

function ViewportPopup({ comp, state, onClose, index }: { comp: TyPopupComp; state: string; onClose: () => void; index: number }) {
    const alignMap: Record<string, CSSProperties> = {
        center: { alignItems: "center", justifyContent: "center" },
        top: { alignItems: "flex-start", justifyContent: "center" },
        bottom: { alignItems: "flex-end", justifyContent: "center" },
        end: { alignItems: "stretch", justifyContent: "flex-end" },
    };

    return (
        <div className="popup-viewport-wrap" style={{ ...alignMap[comp.place ?? "center"], zIndex: `100${index}` }}>
            <div className="popup-content" data-popup-anim={comp.anim} data-state={state}>
                <comp.Component onClose={onClose} details={comp.details ?? null} />
            </div>
        </div>
    );
}

function AnchorPopup({ comp, state, onClose, index }: { comp: TyPopupComp; state: string; onClose: () => void; index: number }) {
    const anchor = comp.anchor as TyAnchorRect;
    const GAP = 4;
    const MARGIN = 8;

    const anchorCenterX = (anchor.left + anchor.right) / 2;
    const alignRight = anchorCenterX >= window.innerWidth / 2;

    const [posStyle, setPosStyle] = useState<CSSProperties>(() => {
        const s: CSSProperties = { minWidth: anchor.width };
        if (comp.place === "anchor-below") s.top = anchor.bottom + GAP;
        else s.bottom = window.innerHeight - anchor.top + GAP;
        if (alignRight) s.right = window.innerWidth - anchor.right;
        else s.left = anchor.left;
        return s;
    });

    const [popupAnim, setPopupAnim] = useState(comp.anim ?? "fade");
    const wrapRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        if (comp.place !== "anchor-below") return;
        const el = wrapRef.current;
        if (!el) return;

        // offsetHeight is not affected by CSS transforms (scale(0)), so it gives the real layout height
        const popupH = el.offsetHeight;
        if (!popupH) return;

        const bottomEdge = anchor.bottom + GAP + popupH;
        if (bottomEdge > window.innerHeight - MARGIN) {
            const topAbove = Math.max(MARGIN, anchor.top - GAP - popupH);
            setPosStyle((prev) => ({ ...prev, top: topAbove }));
            setPopupAnim("scaleBottom");
        }
    }, []);

    return (
        <div ref={wrapRef} className="popup-anchor-wrap" style={{ ...posStyle, zIndex: `100${index}` }}>
            <div className="popup-content" data-popup-anim={popupAnim} data-state={state}>
                <comp.Component onClose={onClose} details={comp.details ?? null} />
            </div>
        </div>
    );
}
